"use client";

import * as React from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { cn } from "@/lib/utils";

const SimpleSelect = SelectPrimitive.Root;

function SimpleSelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return <SelectPrimitive.Group data-slot="select-group" className={cn("scroll-my-1 p-1", className)} {...props} />;
}

function SimpleSelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value data-slot="select-value" className={cn("flex flex-1 text-left", className)} {...props} />
  );
}

function SimpleSelectTrigger({ className, children, ...props }: SelectPrimitive.Trigger.Props) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        // Minimal layout: just items centered with a small gap. No borders or padding.
        "flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap outline-none transition-opacity",
        // Focus state: added a minimal ring so it remains accessible without needing a border
        "rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        // Disabled state
        "disabled:cursor-not-allowed disabled:opacity-50",
        // Icon scaling: set to 1em so the chevron scales perfectly if you increase the font size
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:w-[1em] [&_svg:not([class*='size-'])]:h-[1em]",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          // The opacity-50 keeps the arrow subtle so the word stands out
          <ChevronDownIcon className="pointer-events-none opacity-50" />
        }
      />
    </SelectPrimitive.Trigger>
  );
}

function SimpleSelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start", // Changed default to 'start' so it aligns nicely with the text
  alignOffset = 0,
  alignItemWithTrigger = true,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<SelectPrimitive.Positioner.Props, "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger">) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-2000"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            "relative isolate z-2000 max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          )}
          {...props}
        >
          <SimpleSelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SimpleSelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SimpleSelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn("px-1.5 py-1 text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}

function SimpleSelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
            <CheckIcon className="pointer-events-none" />
          </span>
        }
      />
    </SelectPrimitive.Item>
  );
}

function SimpleSelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function SimpleSelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-2000 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SimpleSelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-2000 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  );
}

const SimpleSelectNamespace = Object.assign(SimpleSelect, {
  Content: SimpleSelectContent,
  Group: SimpleSelectGroup,
  GroupLabel: SimpleSelectLabel,
  Item: SimpleSelectItem,
  ScrollDownButton: SimpleSelectScrollDownButton,
  ScrollUpButton: SimpleSelectScrollUpButton,
  Separator: SimpleSelectSeparator,
  Trigger: SimpleSelectTrigger,
  Value: SimpleSelectValue,
});

export interface SimpleSelectItem {
  label: React.ReactNode;
  value: string | undefined;
}

export interface DataSelectProps {
  /** Array of options to render */
  items: SimpleSelectItem[];
  /** The controlled value of the select */
  value?: string;
  /** Callback when the value changes */
  onValueChange?: (value: string | undefined) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Classes applied to the trigger (use this for your font/weight overrides) */
  className?: string;
  /** Horizontal alignment of the dropdown relative to the trigger */
  align?: SelectPrimitive.Positioner.Props["align"];
}

export function SimpleSelectWrapper({
  items,
  value,
  onValueChange,
  placeholder = "Select...",
  className,
  align = "start",
}: DataSelectProps) {
  return (
    <SimpleSelect value={value} onValueChange={i => onValueChange && onValueChange(i || undefined)}>
      <SimpleSelectTrigger>
        <SimpleSelectValue placeholder={placeholder} className={className}>
          {items.find(i => i.value == value)?.label}
        </SimpleSelectValue>
      </SimpleSelectTrigger>

      <SimpleSelectContent align={align} alignItemWithTrigger={align === "start"}>
        {items.map(item => (
          <SimpleSelectItem key={item.value} value={item.value}>
            {item.label}
          </SimpleSelectItem>
        ))}
      </SimpleSelectContent>
    </SimpleSelect>
  );
}

export { SimpleSelectNamespace as SimpleSelectPrimitive, SimpleSelectWrapper as SimpleSelect };
